import React, { useEffect, useState } from 'react';
import { Button, PdficonColorIcon, Tooltip } from 'ui-components';
import { useGetSavedListCreatorsAll } from '~/hooks/api/useSavedLists';

import { useDownloadPdf } from './useDownloadPdf';
import { mapPdfData } from './utils';

interface Props {
  fileName: string;
  listId?: string;
  agencyName?: string;
  agencyLogo?: string;
  listName?: string;
  enabled?: boolean;
  fullWidth?: boolean;
  isButtonDisabled?: boolean;
  disabledTooltip?: string;
}

export const DownloadPdf: React.FC<Props> = ({
  fileName,
  listId,
  agencyName,
  agencyLogo,
  listName,
  enabled = true,
  fullWidth = false,
  isButtonDisabled,
  disabledTooltip,
}: Props) => {
  const downloadPdfMutation = useDownloadPdf();
  const [hasClicked, setHasClicked] = useState(false);

  const { creators, isLoading: isLoadingCreators } = useGetSavedListCreatorsAll(
    {
      id: listId || '',
      enabled: !!listId && enabled,
    },
  );

  // Effect to handle PDF generation when creators are ready after user clicked
  useEffect(() => {
    if (hasClicked && !isLoadingCreators && creators) {
      const pdfData = mapPdfData(creators, agencyName, agencyLogo, listName);
      downloadPdfMutation.mutate({ pdfData, fileName });
      setHasClicked(false); // Reset the click state
    }
  }, [
    hasClicked,
    isLoadingCreators,
    creators,
    agencyName,
    agencyLogo,
    listName,
    fileName,
    downloadPdfMutation,
  ]);

  const handleClick = () => {
    setHasClicked(true);
  };

  const isGeneratingPdf = downloadPdfMutation.isLoading;
  const shouldShowLoading = hasClicked || isGeneratingPdf;

  if (disabledTooltip && isButtonDisabled) {
    return (
      <Tooltip label={disabledTooltip} placement="top">
        <div className={fullWidth ? 'w-full' : 'w-1/2'}>
          <Button
            aria-label="Download PDF"
            variant="tertiary"
            leftIcon={<PdficonColorIcon />}
            isDisabled
            className="w-full"
          >
            Download .pdf
          </Button>
        </div>
      </Tooltip>
    );
  }

  return (
    <Button
      aria-label="Download PDF"
      trackId="foam-list-download-pdf"
      variant="tertiary"
      leftIcon={<PdficonColorIcon />}
      onClick={handleClick}
      isDisabled={shouldShowLoading}
      isLoading={shouldShowLoading}
      className={fullWidth ? 'w-full' : 'w-1/2'}
    >
      Download .pdf
    </Button>
  );
};
